<template>
  <div
      v-if="!finished"
      class="fixed inset-0 z-50 flex flex-col items-center justify-center px-6 text-center backdrop-blur-sm"
      :class="[backgroundAuraClass || 'bg-black']"
  >
    <div v-if="recognitionSigil && showSigil" class="mb-8 text-4xl opacity-70">{{ recognitionSigil }}</div>

    <div v-if="displayedLines.length > 0">
      <p
          v-for="(line, index) in displayedLines"
          :key="index"
          class="mb-4 text-xl md:text-2xl lg:text-3xl font-light tracking-wide text-white shadow-sm"
          :style="{ opacity: index < visibleCount ? 1 : 0, transition: 'opacity 0.7s ease' }"
          v-html="formatLine(line)"
      />
    </div>
  </div>
</template>


<script setup>
import { ref, watch, onMounted, computed } from 'vue'

const props = defineProps({
  gateScroll: {
    type: Object,
    default: () => ({})
  },
  backgroundAuraClass: {
    type: String,
    default: ''
  },
  timingPattern: {
    type: String,
    default: 'standard'
  },
  recognitionSigil: {
    type: String,
    default: ''
  },
  onComplete: {
    type: Function,
    default: null
  }
})

const displayedLines = ref([])
const visibleCount = ref(0)
const finished = ref(false)
const scriptSections = ref([])
const showSigil = ref(true)

const timingFactors = computed(() => {
  switch (props.timingPattern) {
    case 'welcoming':
      return { basePause: 500, wordFactor: 90, charFactor: 25, minTime: 700, maxTime: 3500 }
    case 'contemplative':
      return { basePause: 800, wordFactor: 120, charFactor: 35, minTime: 1000, maxTime: 5000 }
    case 'inquisitive':
      return { basePause: 600, wordFactor: 100, charFactor: 30, minTime: 800, maxTime: 4000 }
    default: // standard
      return { basePause: 600, wordFactor: 100, charFactor: 30, minTime: 800, maxTime: 4000 }
  }
})

onMounted(() => {
  prepareScript()
  revealLines()
})

watch(() => [props.gateScroll], () => {
  prepareScript()
  revealLines()
}, { deep: true })

function prepareScript() {
  const raw = props.gateScroll || []
  if (!Array.isArray(raw)) {
    scriptSections.value = []
  } else if (raw.every(line => typeof line === 'string')) {
    scriptSections.value = [raw]
  } else if (raw.every(section => Array.isArray(section))) {
    scriptSections.value = raw
  } else {
    scriptSections.value = []
  }
}

// KNOWN GAP, deliberately deferred: unsanitized v-html. Lower-risk than Sacred
// Flame whispers (this is service-driven "gate scroll" copy via
// SacredRecognitionService, not raw AI text), but a real gap — same shape as
// the one closed in ONGOING_FIRE_ARCHITECTURE.md's markdown-rendering pass.
// If this line ever needs a sanitized markdown pipeline, reuse
// app/frontend/services/renderMarkdown.js rather than hand-rolling another one.
function formatLine(line) {
  return line.replace(/\*([^*]+)\*/g, '<em>$1</em>')
}

async function revealLines() {
  const { basePause, wordFactor, charFactor, minTime, maxTime } = timingFactors.value
  const sectionsCount = scriptSections.value.length

  if (showSigil.value) {
    await new Promise(resolve => setTimeout(resolve, basePause * 2))
    showSigil.value = false
  }

  for (let i = 0; i < sectionsCount; i++) {
    const section = scriptSections.value[i]
    if (!Array.isArray(section)) continue

    // Pre-populate all lines as invisible so full section height is reserved before
    // any line fades in — prevents the centered container from jumping as lines appear
    displayedLines.value = section.filter(line => typeof line === 'string')
    visibleCount.value = 0

    for (let j = 0; j < displayedLines.value.length; j++) {
      visibleCount.value = j + 1

      const line = displayedLines.value[j]
      const wordCount = line.split(/\s+/).length
      const charCount = line.length
      const displayTime = Math.min(Math.max((wordCount * wordFactor) + (charCount * charFactor), minTime), maxTime)

      await new Promise(resolve => setTimeout(resolve, displayTime + basePause))
    }

    // Fade all lines out before clearing for the next section
    visibleCount.value = 0
    await new Promise(resolve => setTimeout(resolve, 700))
    displayedLines.value = []
    await new Promise(resolve => setTimeout(resolve, basePause))
  }

  showSigil.value = true
  await new Promise(resolve => setTimeout(resolve, basePause * 2))
  finished.value = true

  props.onComplete?.()
}
</script>

<style scoped>
em {
  @apply font-normal text-amber-200;
}
</style>
